import { relativeWeekend, weekendPlacement, weekendLabel } from '@/lib/planning/weekend';
import { PhonePaneSwitch } from '@/shell/PhonePaneSwitch';
import { useAssistantLauncher } from '@/contexts/AssistantLaunchContext';
// src/apps/tasks/TaskDetailPanel.tsx
//
// The Shell's global detail panel for the Tasks app. Driven by the URL
// ?detail=<kind>:<id> selection (SelectionProvider). Branches on selection.kind
// and renders the matching panel-optimized surface panel inside a fixed
// side-panel chrome:
//
//   task    -> TapContextPanel   (full task surface — unchanged from pre-Shell)
//   routine -> TapRoutinePanel
//   event   -> TapEventPanel
//   meal    -> TapMealPanel       (event id `meal:<entryId>`)
//
// All kinds share the same fixed right-side aside + click-outside-to-close
// behavior (PanelChrome). The full-page task editor (TaskViewRedesign via
// TaskViewContainer) is NOT used here — it's a multi-column page layout that
// collapses/overlaps at ~480px panel width; that lives at the full-page route.
//
// The exact prop wiring for routine/event/meal mirrors the pre-Shell App.tsx
// (the panels themselves are reused verbatim — see src/components/surface/).
// Vault integration is intentionally NOT wired (vault is being removed):
// onSaveNoteToVault is omitted so the "Save to vault" affordance stays hidden.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { showToast } from '@/hooks/useToast';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useSelection } from '@/shell/providers/SelectionProvider';
import { useSideColumnSlot } from '@/shell/SideColumn';
import { createPortal } from 'react-dom';
import type { SelectionRef } from '@/shell/types';
import { useSupabaseTasks } from '@/hooks/useSupabaseTasks';
import { useGatedTaskActions } from '@/hooks/useGatedTaskActions';
import { useContacts } from '@/hooks/useContacts';
import { useProjects } from '@/hooks/useProjects';
import { TaskTimingMenu } from '@/components/plan/TaskTimingMenu';
import { makeTaskAGoal } from '@/components/plan/MakeGoalControl';
import { goalConversion } from '@/lib/planning/goalConversion';
import { PartOfGoalControl } from '@/components/plan/PartOfGoalControl';
import { useDomain } from '@/hooks/useDomain';
import { filterTasksForLayers } from '@/lib/today/domainFilter';
import { useHouseholdSeasons } from '@/hooks/useHouseholdSeasons';
import { weekStartAnchor, readCadenceConfig } from '@/lib/cadence/config';
import { useGoogleCalendar, CalendarReconnectError, type GoogleCalendarInfo, type CalendarEvent } from '@/hooks/useGoogleCalendar';
import { eventMoveErrorMessage } from '@/lib/calendar/moveEvent';
import { useEventNotes, type EventNote } from '@/hooks/useEventNotes';
import { isEventFree, freeKeyFor, seriesKey } from '@/lib/today/eventFree';
import { useEventDiscussionFlags } from '@/hooks/useEventDiscussionFlags';
import { useActionableInstances } from '@/hooks/useActionableInstances';
import { useRoutines } from '@/hooks/useRoutines';
import { useGoals } from '@/hooks/useGoals';
import { useFamilyMembers } from '@/hooks/useFamilyMembers';
import { memberForAuthUser } from '@/lib/scope';
import { useListsContext } from '@/contexts/ListsContext';
import { useSendTaskToBuy } from '@/hooks/useSendTaskToBuy';
import { usePinnedItems } from '@/hooks/usePinnedItems';
import { useMealEventsForDate } from '@/shell/providers/MealEventsProvider';
import { removeFromGroup, ungroupTasks, deleteTaskGroup } from '@/lib/today/groupTasks';
import { TapContextPanel } from '@/components/surface/TapContextPanel';
import { useHorizonPanel } from '@/components/plan/constellation/horizonPanel';
import { TapRoutinePanel } from '@/components/surface/TapRoutinePanel';
import { TapStepPanel } from '@/components/surface/TapStepPanel';
import { groupRoutineSteps } from '@/lib/today/routineCollections';
import { nextStepOrder } from '@/lib/today/stepOrdering';
import type { Routine } from '@/types/actionable';
import { TapEventPanel } from '@/components/surface/TapEventPanel';
import { TapMealPanel } from '@/components/surface/TapMealPanel';
import { applyTriageWhen, describeTriageWhen } from '@/lib/triage/applyWhen';
import { formatDateLabel } from '@/lib/dateHelpers';
import { consumeDiscussionOpen } from '@/lib/discussions/openIntent';
import type { Task, TaskLink } from '@/types/task';
import { enrichLink } from '@/lib/enrichLink';
import { useCanvasActivity } from '@/contexts/CanvasActivityContext';
import { waitingUpdates, CLEAR_WAITING, checkBackLabel } from '@/lib/today/waiting';
import { viewedDayFromParam, isNeededOn, neededTodayToggle, isOnDay, dayRemovalWithUndo } from '@/lib/today/itemPanelDay';
import { isSameDay } from '@/lib/dateUtils';
import { bringBack } from '@/lib/planning/letGo';

/** Find a task by id, searching one level of nested subtasks (group children). */
function findTask(tasks: Task[], id: string): Task | null {
  for (const t of tasks) {
    if (t.id === id) return t;
    const child = t.subtasks?.find((s) => s.id === id);
    if (child) return child;
  }
  return null;
}

const panelClassName =
  // Phone: a full-screen modal, so it sits above the bottom tab bar (z-40).
  'fixed right-0 top-0 z-30 max-md:z-[45] h-screen w-full md:w-[480px] border-l border-neutral-200 bg-bg-elevated overflow-y-auto shadow-xl';

// Selectors for elements whose mousedown must NOT dismiss the open detail panel:
// interactive controls, selectable cards, and popovers that render outside the
// panel DOM (card action buttons + their portaled menus, and the panel's own
// portaled popovers). Without this, pressing a card's Reschedule/Add-Project/⋯/
// Context button while the panel is open is read as a "click away" and just
// closes the panel instead of running the action (and the panel should stay
// open through the interaction).
const PANEL_KEEPALIVE_SELECTOR =
  '[data-selectable], [data-panel-keepalive], button, a, input, textarea, select, [role="menu"], [role="menuitem"], [role="dialog"], [role="listbox"], [role="option"]';

/**
 * True when a mousedown on `target` (which is outside `panelEl`) should dismiss
 * the panel — i.e. it landed on genuinely neutral chrome, not on a card, a
 * control, or a popover. Exported for unit testing.
 */
export function shouldDismissPanel(target: HTMLElement | null, panelEl: HTMLElement | null): boolean {
  if (!target) return false;
  if (panelEl && panelEl.contains(target)) return false;
  if (target.closest(PANEL_KEEPALIVE_SELECTOR)) return false;
  return true;
}

/**
 * Shared side-panel chrome: the fixed right-side aside + click-outside-to-close
 * + Escape-to-close (useEscapeKey — a nested popover or wide Notes consumes
 * its own Escape first; a focused field is left before the panel closes).
 * Every kind renders through this so the close behavior is identical (and so
 * clicking another card still switches selection — the card's click fires
 * setSelection after this clears, landing on the new item).
 */
function PanelChrome({ children }: { children: React.ReactNode }) {
  const { clearSelection } = useSelection();
  const { openAssistant } = useAssistantLauncher();
  const panelRef = useRef<HTMLElement>(null);
  // Desktop draws inside the shared Details/AI column; while the AI pane is
  // in front the panel stays mounted (edits kept) but must not take Escape.
  const { hosted, detailsSlot, detailsVisible } = useSideColumnSlot();
  const fullScreen = useMediaQuery('(max-width: 767px)');
  const inColumn = !fullScreen && !!detailsSlot;
  // The column opens with the selection; draw once its slot exists rather
  // than flashing the old fixed panel for a frame.
  const waiting = hosted && !fullScreen && !detailsSlot;
  useEscapeKey(detailsVisible, clearSelection);
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (shouldDismissPanel(e.target as HTMLElement | null, panelRef.current)) {
        clearSelection();
      }
    }
    const id = window.setTimeout(() => document.addEventListener('mousedown', onDown), 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('mousedown', onDown);
    };
  }, [clearSelection]);
  // Keyboard/screen-reader users land in the panel they just opened, and go
  // back to the row that opened it on close. A child that autofocuses its own
  // field (a fresh title) keeps that focus.
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = panelRef.current;
    if (!panel) return;
    if (!panel.contains(document.activeElement)) panel.focus({ preventScroll: true });
    return () => {
      const active = document.activeElement;
      const focusWasInPanel = !active || active === document.body || !!panel?.contains(active) || !active.isConnected;
      if (focusWasInPanel && opener?.isConnected && opener !== document.body) opener.focus({ preventScroll: true });
    };
  }, [waiting]);
  // Full-screen on phones, so it is a modal dialog there; a side panel beside
  // the page on wider screens.
  const aside = (
    <aside
      ref={panelRef}
      data-testid="task-detail-panel"
      className={inColumn ? 'h-full overflow-y-auto bg-bg-elevated' : panelClassName}
      tabIndex={-1}
      aria-label="Details"
      role={fullScreen ? 'dialog' : undefined}
      aria-modal={fullScreen ? true : undefined}
    >
      {/* Phone: the same Details | AI switch as the desktop column. */}
      {fullScreen && <PhonePaneSwitch active="details" onChange={(pane) => { if (pane === 'ai') openAssistant(); }} />}
      {children}
    </aside>
  );
  if (waiting) return null;
  return inColumn ? createPortal(aside, detailsSlot) : aside;
}

function PanelLoading() {
  return <div className="p-8 text-center text-neutral-500">Loading…</div>;
}

/**
 * True when an open event panel points at an event that's no longer resolvable
 * and should be closed rather than left hanging on "Loading…".
 *
 * `events` is replaced wholesale on each day's fetch (scoped to the viewed day's
 * window — see useGoogleCalendar.fetchEvents), so an event rescheduled to another
 * day or deleted leaves the set while its id stays in ?detail=event:<id>. We only
 * close once the calendar has settled (not fetching/loading) AND the day loaded
 * at least one event — proof a fetch completed and this id genuinely isn't in it.
 * Without the `eventCount > 0` guard we'd close prematurely during the pre-fetch
 * tick on a fresh deep-link. Exported for unit testing.
 */
export function shouldCloseStaleEventPanel(opts: {
  found: boolean;
  isFetching: boolean;
  isLoading: boolean;
  eventCount: number;
}): boolean {
  const { found, isFetching, isLoading, eventCount } = opts;
  return !found && !isFetching && !isLoading && eventCount > 0;
}

// ── Task ──────────────────────────────────────────────────────────────────
function TaskPanelBody({ id }: { id: string }) {
  const autoOpenDiscussion = useAutoOpenDiscussion('task', id);
  const { clearSelection } = useSelection();
  const navigate = useNavigate();

  const { tasks, letGoTasks, addTask, addSubtask, deleteTask, toggleTask, updateTask: rawUpdateTask, updateTasksBulk, pushTask, setBucket, setGoal, setGoalLink, refetch, keepForward, dropCommitment } = useSupabaseTasks();
  // Lists get live tasks only; a let-go item opened from search is found here.
  const findAny = useCallback((tid: string) => findTask(tasks, tid) ?? findTask(letGoTasks ?? [], tid), [tasks, letGoTasks]);
  // "Part of…" offers the goals in the life areas in view — the same lens
  // every list applies (useDomain layers).
  const { layers } = useDomain();
  const goalCandidates = useMemo(() => filterTasksForLayers(tasks, layers), [tasks, layers]);
  const { contacts, addContact, searchContacts } = useContacts();
  // The "To buy" conversion the row's strip used to offer on Today. The task
  // is deleted when it moves (an item lives in one place), so the panel
  // closes with it; the toast names where it went.
  const { lists, addList } = useListsContext();
  const sendTaskToBuy = useSendTaskToBuy({ tasks, lists, addList, deleteTask, addTask });
  const { projects } = useProjects();
  const { events } = useGoogleCalendar();
  const { members: familyMembers } = useFamilyMembers();
  const pinnedItems = usePinnedItems();
  const { seasons } = useHouseholdSeasons();
  // Yearly intentions, so What you'll need can show resources from the top of the plan.
  const { goals } = useGoals();

  // Iris's rule: any process on an Unsorted item has to involve giving it a
  // domain — this panel is the other global mutation surface besides
  // Today/Inbox/the horizon pages, so it gets the same gate. `findTaskById`
  // reuses `findTask` (below) so a subtask id resolves too (onRescheduleSubtask
  // acts on children, which live one level down in `t.subtasks`, not in the
  // top-level `tasks` array). `raw` is memoized on its own stable
  // (useCallback-wrapped) members for referential stability. `updateTask` is
  // shadowed to the gated version so every existing call site below — not
  // just the four the gate was written for — goes through it uniformly.
  const findTaskById = useCallback((tid: string) => findAny(tid) ?? undefined, [findAny]);
  const gatedRaw = useMemo(
    () => ({ updateTask: rawUpdateTask, pushTask, updateTasksBulk, setBucket }),
    [rawUpdateTask, pushTask, updateTasksBulk, setBucket],
  );
  const gated = useGatedTaskActions(gatedRaw, findTaskById);
  const updateTask = gated.updateTask;

  // Meal-plan entries synthesized as CalendarEvent objects, for linked-event
  // resolution (mirrors the legacy `eventsWithMeals`).
  const today = useMemo(() => new Date(), []);
  const mealEvents = useMealEventsForDate(today);
  const eventsWithMeals = useMemo(() => [...events, ...mealEvents], [events, mealEvents]);

  const task = useMemo(() => findAny(id), [findAny, id]);

  const handleClose = useCallback(() => clearSelection(), [clearSelection]);

  // The day Today is showing (its `?date=` contract) — what "Need today" and
  // "Remove from this day" mean, as they did on the row menu.
  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get('date');
  const viewedDay = useMemo(() => viewedDayFromParam(dateParam), [dateParam]);
  const { run } = useCanvasActivity();
  // Opened over a Month or Season page: Later / Let go for that period only
  // (paper-style horizons, 2026-11-04).
  const horizon = useHorizonPanel(task, { tasks, seasons, updateTask, keepForward, dropCommitment });

  if (!task) return <PanelLoading />;

  const dayName = isSameDay(viewedDay, new Date())
    ? 'today'
    : viewedDay.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  const childIds = (task.subtasks ?? []).map((s) => s.id);

  return (
    <TapContextPanel
      task={task}
      goals={goals}
      contacts={contacts}
      projects={projects}
      events={eventsWithMeals}
      familyMembers={familyMembers}
      siblingTaskCandidates={tasks}
      allTasks={tasks}
      // The why-chain (Task → Project → Goal) is not passed any more: Projects
      // are hidden from the product (2026-09-02 — see the note in Sidebar.tsx)
      // and WhyChain renders nothing without one. Component + test are parked.
      // createdByName not tracked in current data model
      onAssistMutate={refetch}
      autoOpenDiscussion={autoOpenDiscussion}
      onSendToBuy={() => {
        void (async () => {
          const result = await sendTaskToBuy(task.id);
          if (!result) return;
          showToast(`"${result.itemText}" moved to To buy`, 'success');
          handleClose();
        })();
      }}
      onClose={handleClose}
      onTitleChange={(t) => updateTask(task.id, { title: t })}
      onNotesChange={(n) => updateTask(task.id, { notes: n })}
      onPhoneChange={(v) => updateTask(task.id, { phoneNumber: v })}
      onEmailChange={(v) => updateTask(task.id, { email: v })}
      // onSaveNoteToVault intentionally omitted (vault integration removed)
      onToggleComplete={() => toggleTask(task.id)}
      // What the task is FOR — the connected-planning contract: details lead
      // with the goal it serves, then its when. "Part of…" says which goal
      // and changes it (walkthrough 2026-10-02, #26); it is the Goal row's
      // value now (detail pane standard, 2026-10-19).
      purpose={task.isGoal ? undefined : (
        <PartOfGoalControl
          compact
          task={task}
          tasks={tasks}
          choiceTasks={goalCandidates}
          seasons={seasons}
          setGoalLink={setGoalLink}
          onOpenGoal={(gid) => navigate(`/task/${gid}`)}
        />
      )}
      // "Make it a goal" changes the same row into a goal, and says why when
      // it cannot (Scott, 2026-09-25) — offered in the ⋯ rather than as a
      // link beside the goal it would stop being part of.
      onMakeGoal={task.isGoal || task.completed ? undefined : () => {
        const check = goalConversion(task, tasks);
        if (check.ok) void makeTaskAGoal(task, setGoal);
        else showToast(check.reason, 'info', 6000);
      }}
      // A goal is not scheduled, so it gets no timing control.
      timingControl={task.isGoal ? undefined : (
        <TaskTimingMenu
          task={task}
          onUpdateTask={gated.updateTask}
          // The task's OWN period, never the clock's: an October step opened
          // in September must offer October's weeks (the S2-17 class).
          periodStart={task.weekStart ?? task.monthStart ?? task.scheduledFor ?? new Date()}
          fallbackWeekStart={task.weekStart ?? weekStartAnchor(task.monthStart ?? task.scheduledFor ?? new Date(), readCadenceConfig().weekStartsOn)}
        />
      )}
      onSchedule={(date, isAllDay) => {
        void (async () => {
          const result = await updateTask(task.id, { bucket: 'timed', scheduledFor: date, isAllDay });
          // A cancelled domain gate writes nothing — no confirmation for a
          // move that didn't happen.
          if (result === false) return;
          const label = isAllDay
            ? formatDateLabel(date)
            : `${formatDateLabel(date)}, ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
          showToast(`Moved to ${label}`, 'success');
        })();
      }}
      onReschedule={(when) => {
        // Gated pushTask/setBucket (not a hand-rolled bucket/scheduledFor
        // write) — same reason as horizons/shared.tsx's applyWhen: this is
        // the real hook logic (defer_count, weekStart, overdue time
        // preservation), and an Unsorted task reschedule here asks first.
        void (async () => {
          if (when === 'this-weekend' || when === 'next-weekend') {
            const saturday = relativeWeekend(when === 'next-weekend');
            const result = await gated.updateTask(task.id, weekendPlacement(saturday));
            if (result !== false) showToast(`${weekendLabel(saturday)} · either day`, 'success');
            return;
          }
          const ok = await applyTriageWhen(when, task.id, { onPushTask: gated.pushTask, onSetBucket: gated.setBucket!, onFocus: (id, day) => gated.updateTask(id, { plannedOn: day }) });
          if (ok) showToast(describeTriageWhen(when), 'success');
        })();
      }}
      // Unschedule clears the day only: the row's week and period commitments
      // stay (D1c.1), and the placement module picks the bucket from them —
      // back to its week list, or the inbox when it has no list at all.
      onClearSchedule={() =>
        updateTask(task.id, { scheduledFor: undefined, isAllDay: undefined })
      }
      isPinned={pinnedItems.isPinned('task', task.id)}
      onTogglePin={() => {
        if (pinnedItems.isPinned('task', task.id)) pinnedItems.unpin('task', task.id);
        else pinnedItems.pin('task', task.id);
      }}
      onDelete={() => {
        deleteTask(task.id);
        handleClose();
      }}
      onOpenContact={(cid) => navigate(`/contacts/${cid}`)}
      onOpenMember={(mid) => navigate(`/family/${mid}`)}
      onOpenProject={(pid) => navigate(`/projects/${pid}`)}
      onOpenEvent={(eid) => navigate(`/today?detail=event:${eid}`)}
      onOpenTask={(tid) => navigate(`/today?detail=task:${tid}`)}
      onOpenRelated={(kind, rid) => {
        if (kind === 'task') navigate(`/today?detail=task:${rid}`);
        // other kinds: no-op for now
      }}
      onToggleSubtask={(sid) => toggleTask(sid)}
      onAddSubtask={(title) => addSubtask(task.id, title)}
      onRemoveSubtask={(sid) => {
        void removeFromGroup(sid, { updateTask, refetch });
      }}
      onRescheduleSubtask={(sid, when) => {
        void (async () => {
          if (when === 'this-weekend' || when === 'next-weekend') {
            const saturday = relativeWeekend(when === 'next-weekend');
            const result = await gated.updateTask(sid, weekendPlacement(saturday));
            if (result !== false) showToast(`${weekendLabel(saturday)} · either day`, 'success');
            return;
          }
          const ok = await applyTriageWhen(when, sid, { onPushTask: gated.pushTask, onSetBucket: gated.setBucket!, onFocus: (id, day) => gated.updateTask(id, { plannedOn: day }) });
          if (ok) showToast(describeTriageWhen(when), 'success');
        })();
      }}
      onScheduleSubtask={(sid, date, isAllDay) =>
        updateTask(sid, { bucket: 'timed', scheduledFor: date, isAllDay })
      }
      onUngroup={() => {
        void ungroupTasks(task.id, childIds, { updateTask, deleteTask, refetch });
        handleClose();
      }}
      onDeleteGroup={() => {
        void deleteTaskGroup(task.id, childIds, { deleteTask, refetch });
        handleClose();
      }}
      onAddLink={(url) => {
        const next: TaskLink[] = [...(task.links ?? []), { url }];
        updateTask(task.id, { links: next });
        // Read the page now, while saving it is still the user's intent —
        // so the facts are on the card by the time the task surfaces.
        enrichLink('task', task.id, url, task.title);
      }}
      onUpdateLocation={(location, placeId) =>
        updateTask(task.id, { location, locationPlaceId: placeId })
      }
      onDirectionsChange={(directions) => updateTask(task.id, { directions })}
      onClearLocation={() =>
        updateTask(task.id, { location: undefined, locationPlaceId: undefined })
      }
      onContextChange={(ctx) => updateTask(task.id, { context: ctx ?? null })}
      onAssigneesChange={(ids) =>
        updateTask(task.id, { assignedToAll: ids.length > 0 ? ids : undefined })
      }
      onContactChange={(cid) => updateTask(task.id, { contactId: cid })}
      onSearchContacts={searchContacts}
      onAddContact={(name, details) => addContact({ name, ...details })}
      // What the Today row's menus did (Today, trimmed, 2026-10-14) — the same
      // writers, through the same gated updateTask.
      onUpdateDiscussion={(next) => updateTask(task.id, next)}
      onSaveWaiting={async (waitingFor, checkBack) => {
        const result = await updateTask(task.id, waitingUpdates(task, waitingFor, checkBack));
        if (result === false) return;
        showToast(checkBack ? `Waiting — back ${checkBackLabel(checkBack)}` : 'Marked waiting', 'success');
      }}
      onClearWaiting={() => updateTask(task.id, CLEAR_WAITING)}
      neededToday={isNeededOn(task, viewedDay)}
      onToggleNeededToday={() => updateTask(task.id, neededTodayToggle(task, viewedDay))}
      removeFromDayLabel={`Remove from ${dayName}`}
      periodActions={horizon.actions}
      // Let go is kept, never shown; opened from search it can come back —
      // to the periods and place it had, with Undo.
      onBringBack={task.dismissedAt && task.category !== 'paper' ? () => bringBack(task, updateTask, run) : undefined}
      onRemoveFromDay={isOnDay(task, viewedDay) ? () => {
        const { updates, previous } = dayRemovalWithUndo(task, viewedDay);
        return run(`Remove “${task.title}” from ${dayName}`, async () => (await updateTask(task.id, updates)) !== false, {
          ids: [task.id],
          undo: async () => (await updateTask(task.id, previous)) !== false,
        });
      } : undefined}
    />
  );
}

// ── Routine ─────────────────────────────────────────────────────────────────
function RoutinePanelBody({ id, date }: { id: string; date?: string }) {
  const autoOpenDiscussion = useAutoOpenDiscussion('routine', id);
  const { clearSelection, setSelection } = useSelection();
  // Search ALL routines (not just active): flipping a routine to "reference"
  // visibility removes it from the active set/timeline but the panel must stay
  // populated so it doesn't go blank (and so the user can flip it back).
  const { routines, updateRoutine, addRoutine, deleteRoutine, refetch: refetchRoutines } = useRoutines();
  const { members: familyMembers, currentUserId } = useFamilyMembers();
  // "I'll do it" on one day: the signed-in person exactly, never a guess.
  const selfMemberId = memberForAuthUser(familyMembers, currentUserId)?.id ?? null;
  // Opened from one day (Week, Routines): the "<day> only" section is about it.
  const occurrenceDate = useMemo(() => {
    if (!date) return undefined;
    const [y, m, d] = date.split('-').map(Number);
    return new Date(y, m - 1, d);
  }, [date]);

  const routine = useMemo(() => routines.find((r) => r.id === id), [routines, id]);
  // Steps of this collection, sorted the same way /routines sorts them.
  const steps = useMemo(
    () => groupRoutineSteps(routines).collections.find((c) => c.id === id)?.steps ?? [],
    [routines, id],
  );
  const parent = useMemo(
    () => (routine?.parent_routine_id ? routines.find((r) => r.id === routine.parent_routine_id) : undefined),
    [routines, routine],
  );
  const handleClose = useCallback(() => clearSelection(), [clearSelection]);

  if (!routine) return <PanelLoading />;

  // A collection STEP opens the step panel (doses, instructions, remove/delete)
  // with back-navigation to its parent — mirrors /routines.
  if (parent) {
    return (
      <TapStepPanel
        step={routine}
        parentName={parent.name}
        onClose={() => setSelection({ kind: 'routine', id: parent.id })}
        onRename={(name) => updateRoutine(routine.id, { name })}
        onDosesChange={(times) => updateRoutine(routine.id, { times_per_day: times })}
        onNotesChange={(description) => updateRoutine(routine.id, { description })}
        onScheduleChange={(pattern) => updateRoutine(routine.id, { recurrence_pattern: pattern })}
        onTargetChange={(t) => updateRoutine(routine.id, { target_amount: t?.amount ?? null, target_unit: t?.unit ?? null })}
        onPromote={() => {
          updateRoutine(routine.id, { parent_routine_id: null, step_order: null });
          setSelection({ kind: 'routine', id: parent.id });
        }}
        onDelete={() => {
          deleteRoutine(routine.id);
          setSelection({ kind: 'routine', id: parent.id });
        }}
      />
    );
  }

  return (
    <TapRoutinePanel
      routine={routine}
      familyMembers={familyMembers}
      onClose={handleClose}
      viewedDate={occurrenceDate}
      occurrenceDate={occurrenceDate}
      selfMemberId={selfMemberId}
      {...(steps.length > 0
        ? {
            steps,
            onSelectStep: (s: Routine) => setSelection({ kind: 'routine', id: s.id }),
            onAddStep: (name: string) =>
              addRoutine({ name, parent_routine_id: routine.id, step_order: nextStepOrder(steps) }),
            onReorderSteps: (writes: { id: string; step_order: number }[]) => {
              for (const w of writes) updateRoutine(w.id, { step_order: w.step_order });
            },
          }
        : {})}
      onRename={(name) => updateRoutine(routine.id, { name })}
      onNotesChange={(n) => updateRoutine(routine.id, { description: n })}
      onContextChange={(ctx) => updateRoutine(routine.id, { context: ctx ?? null })}
      onVisibilityChange={(v) => updateRoutine(routine.id, { visibility: v })}
      onShowOnTodayChange={(next) => updateRoutine(routine.id, { show_on_timeline: next })}
      onShowOnKioskChange={(next) => updateRoutine(routine.id, { show_on_kiosk: next })}
      onAssignChange={(ids) => updateRoutine(routine.id, { assigned_to_all: ids })}
      onTargetChange={(t) => updateRoutine(routine.id, { target_amount: t?.amount ?? null, target_unit: t?.unit ?? null })}
      onScheduleChange={(pattern, timeOfDay) =>
        updateRoutine(routine.id, {
          recurrence_pattern: pattern,
          time_of_day: timeOfDay || null,
        })
      }
      onUpdateLocation={(location, placeId) =>
        updateRoutine(routine.id, { location, location_place_id: placeId ?? null })
      }
      onClearLocation={() =>
        updateRoutine(routine.id, { location: null, location_place_id: null })
      }
      onAssistMutate={refetchRoutines}
      autoOpenDiscussion={autoOpenDiscussion}
    />
  );
}

/** Midnight of the event's start day, for a day-scoped refetch after a move. */
function getEventDayStart(event: CalendarEvent): Date | null {
  const raw = event.start_time ?? event.startTime;
  if (!raw) return null;
  const d = new Date(raw);
  d.setHours(0, 0, 0, 0);
  return d;
}

// Human-readable reasons a Google Calendar write can fail. 403 means the event
// lives on a calendar the user can't edit (an invite / shared / subscribed
// calendar) — say that plainly instead of a generic failure.
// One wording for a refused calendar edit, shared with the week's drag: a 403
// says the edit was refused, not why, and a shared calendar can permit edits
// (Codex, 2026-09-24). See lib/calendar/moveEvent.
const eventUpdateErrorMessage = eventMoveErrorMessage;

// ── Event ─────────────────────────────────────────────────────────────────
function EventPanelBody({ id }: { id: string }) {
  const autoOpenDiscussion = useAutoOpenDiscussion('event', id);
  const { clearSelection } = useSelection();
  const navigate = useNavigate();
  const { events, updateEvent, moveEvent, deleteEvent, removeEventLocal, fetchEvents, fetchCalendarList, isFetching, isLoading } = useGoogleCalendar();
  const { getNote, updateNote, fetchNote, addEventLink, updateEventFree } = useEventNotes();
  const { tasks, addPrepTask, toggleTask } = useSupabaseTasks();
  const {
    isFlagged,
    getFlag,
    flagEvent,
    unflagEvent,
    updateNote: updateDiscussionNote,
  } = useEventDiscussionFlags();

  // The calendar list tells us the event's access level (Google rejects writes
  // to view-only calendars) and which calendars it could move to.
  const [calendarList, setCalendarList] = useState<GoogleCalendarInfo[]>([]);
  useEffect(() => {
    let cancelled = false;
    fetchCalendarList().then((cals) => { if (!cancelled) setCalendarList(cals); });
    return () => { cancelled = true; };
  }, [fetchCalendarList]);

  // Event selection id is `google_event_id || id` (see CascadingRiverView).
  const event = useMemo(
    () => events.find((e) => (e.google_event_id || e.id) === id),
    [events, id],
  );
  const handleClose = useCallback(() => clearSelection(), [clearSelection]);

  // Warm the event-notes cache. getNote is cache-only and each hook instance
  // starts empty — without this fetch, saved notes/links never displayed.
  useEffect(() => {
    if (event) fetchNote(event.google_event_id || event.id);
  }, [event, fetchNote]);

  // "Free" can live on the SERIES note (recurring_event_id), not just the
  // instance note fetched above — warm that one too (fetchNote is a no-op
  // when the key is already cached, e.g. a non-recurring event) so the pill
  // reflects a flag set on a previous occurrence.
  useEffect(() => {
    if (event) fetchNote(freeKeyFor(event));
  }, [event, fetchNote]);

  // Done state lives in actionable_instances (Symphony-side, never a Google
  // write), keyed by the event's OWN start day — so completing works even when
  // the panel was opened from another day's view. Optimistic toggle; the Today
  // timeline picks the change up via its actionable_instances realtime refresh.
  const { getInstance, markDone, undoDone, skip } = useActionableInstances();
  const [completed, setCompleted] = useState(false);
  const [skipped, setSkipped] = useState(false);
  const instanceDate = useMemo(() => (event ? getEventDayStart(event) : null), [event]);
  useEffect(() => {
    if (!event || !instanceDate) return;
    let cancelled = false;
    getInstance('calendar_event', event.google_event_id || event.id, instanceDate).then((instance) => {
      if (cancelled) return;
      setCompleted(instance?.status === 'completed');
      setSkipped(instance?.status === 'skipped');
    });
    return () => { cancelled = true; };
  }, [event, getInstance, instanceDate]);

  // "Skip today" from the Today row's ⋯ menu (Today, trimmed, 2026-10-14):
  // this occurrence only, on the event's own day, with Undo — the row's
  // writer (useScheduleActions.onSkipEvent) through the canvas receipt.
  const { run } = useCanvasActivity();
  const handleToggleSkip = useCallback(async () => {
    if (!event || !instanceDate) return;
    const eid = event.google_event_id || event.id;
    const day = instanceDate;
    const dayName = isSameDay(day, new Date()) ? 'today' : day.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    const next = !skipped;
    const ok = await run(
      next ? `Skip “${event.title}” ${dayName}` : `Put “${event.title}” back ${dayName}`,
      () => (next ? skip('calendar_event', eid, day) : undoDone('calendar_event', eid, day)),
      {
        ids: [eid],
        undo: async () => {
          const undone = await (next ? undoDone('calendar_event', eid, day) : skip('calendar_event', eid, day));
          if (undone) setSkipped(!next);
          return undone;
        },
      },
    );
    if (ok) setSkipped(next);
  }, [event, instanceDate, skipped, run, skip, undoDone]);

  const handleToggleComplete = useCallback(async () => {
    if (!event || !instanceDate) return;
    const eid = event.google_event_id || event.id;
    const next = !completed;
    setCompleted(next);
    const ok = next
      ? await markDone('calendar_event', eid, instanceDate)
      : await undoDone('calendar_event', eid, instanceDate);
    if (!ok) {
      setCompleted(!next);
      showToast('Could not update the event', 'error');
      return;
    }
    showToast(next ? 'Event completed' : 'Event marked incomplete', 'success');
  }, [event, instanceDate, completed, markDone, undoDone]);

  // Close the panel (rather than hang on "Loading…") when the selected event has
  // been rescheduled off this day or deleted — see shouldCloseStaleEventPanel.
  useEffect(() => {
    if (
      shouldCloseStaleEventPanel({
        found: !!event,
        isFetching,
        isLoading,
        eventCount: events.length,
      })
    ) {
      clearSelection();
    }
  }, [event, isFetching, isLoading, events.length, clearSelection]);

  if (!event) return <PanelLoading />;

  const eventId = event.google_event_id || event.id;

  // "Free" resolution: instance note wins, else the series note. getNote is
  // cache-only — the effects above warm both keys.
  const seriesEventKey = seriesKey(event);
  const freeNotes = new Map<string, EventNote>();
  const instanceNote = getNote(eventId);
  if (instanceNote) freeNotes.set(eventId, instanceNote);
  if (seriesEventKey) {
    const seriesNote = getNote(seriesEventKey);
    if (seriesNote) freeNotes.set(seriesEventKey, seriesNote);
  }
  const isFree = isEventFree(event, freeNotes);

  // Resolve the event's calendar in the account's calendar list. Events with
  // no calendar_id live on the primary calendar (always writable — it's ours).
  const eventCalendarId = event.calendar_id ?? event.calendarId;
  const eventCalendar = calendarList.find((c) =>
    eventCalendarId ? c.id === eventCalendarId : c.primary,
  );
  const calendarAccess = calendarList.length > 0
    ? {
        name: event.calendar_name ?? event.calendarName ?? eventCalendar?.summary ?? null,
        readOnly: eventCalendar ? eventCalendar.accessRole === 'reader' : false,
      }
    : undefined;
  // Move targets: Google moves an event only between calendars of one
  // account, and Outlook (Graph) cannot move one at all.
  const writableCalendars = (eventCalendar?.provider ?? 'google') !== 'google'
    ? []
    : calendarList
      .filter((c) => c.accessRole === 'owner' || c.accessRole === 'writer')
      .filter((c) => (c.provider ?? 'google') === 'google')
      .filter((c) => !eventCalendar?.connectionId || !c.connectionId || c.connectionId === eventCalendar.connectionId)
      .map((c) => ({ id: c.id, summary: c.summary }));

  return (
    <TapEventPanel
      event={event}
      autoOpenDiscussion={autoOpenDiscussion}
      notes={getNote(eventId)?.notes ?? undefined}
      allTasks={tasks}
      completed={completed}
      onToggleComplete={handleToggleComplete}
      skipDay={instanceDate ? {
        skipped,
        label: isSameDay(instanceDate, new Date()) ? 'Skip today' : 'Skip this day',
        onToggle: () => { void handleToggleSkip(); },
      } : undefined}
      calendarAccess={calendarAccess}
      writableCalendars={writableCalendars}
      onMoveToCalendar={async (destinationCalendarId) => {
        try {
          await moveEvent({
            eventId,
            sourceCalendarId: eventCalendarId ?? 'primary',
            destinationCalendarId,
          });
          showToast('Event moved', 'success');
          // Refresh the day so the event reappears under its new calendar.
          const start = getEventDayStart(event);
          if (start) {
            const end = new Date(start); end.setHours(23, 59, 59, 999);
            await fetchEvents(start, end);
          }
        } catch (err) {
          showToast(eventUpdateErrorMessage(err), 'error', 4000);
        }
      }}
      onClose={handleClose}
      onDeleteEvent={async () => {
        try {
          // This instance only for a recurring series (deleteSeries defaults
          // off), with the event's own calendar — a secondary-calendar event
          // sent to 'primary' 404s.
          await deleteEvent({ eventId, calendarId: eventCalendarId });
          // deleteEvent writes to Google only; drop it from the held events
          // so no view keeps drawing it until the next fetch.
          removeEventLocal(eventId);
          showToast('Event deleted', 'success');
          return true;
        } catch (err) {
          console.error('Failed to delete event:', err);
          showToast(
            err instanceof CalendarReconnectError
              ? 'Calendar connection expired — reconnect in Settings'
              : 'Could not delete the event',
            'error',
            4000,
          );
          return false;
        }
      }}
      onNotesChange={(html) => updateNote(eventId, html)}
      onAddPrepTask={(title) => {
        // Prep tasks land on the event's day (a plain timed task linked to it).
        const when = getEventDayStart(event) ?? new Date();
        addPrepTask(title, eventId, when);
      }}
      onTogglePrepTask={(tid) => { void toggleTask(tid); }}
      links={getNote(eventId)?.links}
      onAddLink={(url) => addEventLink(eventId, url)}
      discussion={{ flagged: isFlagged(eventId), note: getFlag(eventId)?.discussionNote }}
      onToggleDiscussion={async (flagged) => {
        if (flagged) {
          await flagEvent(eventId, { title: event.title, calendarId: eventCalendarId ?? undefined });
        } else {
          await unflagEvent(eventId);
        }
      }}
      onDiscussionNoteChange={(note) => updateDiscussionNote(eventId, note)}
      free={isFree}
      freeAppliesToSeries={!!seriesEventKey}
      onToggleFree={(free) => updateEventFree(freeKeyFor(event), free)}
      onOpenTask={(tid) => navigate(`/today?detail=task:${tid}`)}
      onOpenProject={() => {}}
      onOpenRelated={() => {}}
      onUpdateEventLocation={async (eid, location, calendarId) => {
        try {
          await updateEvent({ eventId: eid, location, calendarId });
          showToast('Location updated', 'success');
        } catch (err) {
          showToast(eventUpdateErrorMessage(err), 'error', 4000);
        }
      }}
      onRenameEvent={async (nextTitle) => {
        try {
          await updateEvent({
            eventId: event.google_event_id ?? event.id,
            title: nextTitle,
            calendarId: event.calendar_id ?? event.calendarId,
          });
          showToast('Event renamed', 'success');
        } catch (err) {
          showToast(eventUpdateErrorMessage(err), 'error', 4000);
        }
      }}
      onReschedule={async (startTime, endTime) => {
        try {
          await updateEvent({
            eventId: event.google_event_id ?? event.id,
            startTime,
            endTime,
            calendarId: event.calendar_id ?? event.calendarId,
          });
          showToast('Event updated', 'success');
          return true;
        } catch (err) {
          showToast(eventUpdateErrorMessage(err), 'error', 4000);
          // Said out loud, so the inline editor can keep the edit rather than
          // closing over a change Google refused.
          return false;
        }
      }}
    />
  );
}

// ── Meal ─────────────────────────────────────────────────────────────────
function MealPanelBody({ id }: { id: string }) {
  const { clearSelection } = useSelection();
  // Meal selection id is `meal:<entryId>`; the synthesized event for it is
  // produced by MealEventsProvider for a given date. Meals tapped from a
  // horizon are for the viewed day, so resolve against today's set.
  const today = useMemo(() => new Date(), []);
  const mealEvents = useMealEventsForDate(today);

  const event = useMemo(() => mealEvents.find((e) => e.id === id), [mealEvents, id]);
  const handleClose = useCallback(() => clearSelection(), [clearSelection]);

  if (!event) return <PanelLoading />;

  return <TapMealPanel event={event} onClose={handleClose} />;
}

/**
 * `?discuss=1` (the Discussions inbox deep link) opens the item's Discussion
 * as the panel mounts, then leaves the URL — a reload shouldn't reopen it.
 * A row's discussion bubble asks the same thing without the URL
 * (`requestDiscussionOpen`, consumed here once for this item).
 */
function useAutoOpenDiscussion(kind: 'task' | 'routine' | 'event', id: string): boolean {
  const [searchParams, setSearchParams] = useSearchParams();
  const wanted = searchParams.get('discuss') === '1';
  const [auto] = useState(() => wanted || consumeDiscussionOpen(kind, id));
  useEffect(() => {
    if (!wanted) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('discuss');
      return next;
    }, { replace: true });
  }, [wanted, setSearchParams]);
  return auto;
}

export function TaskDetailPanel({ selection }: { selection: SelectionRef }) {
  return (
    <PanelChrome>
      {selection.kind === 'task' ? (
        <TaskPanelBody id={selection.id} />
      ) : selection.kind === 'routine' ? (
        <RoutinePanelBody id={selection.id} date={selection.date} />
      ) : selection.kind === 'event' ? (
        <EventPanelBody id={selection.id} />
      ) : selection.kind === 'meal' ? (
        <MealPanelBody id={selection.id} />
      ) : (
        <PanelLoading />
      )}
    </PanelChrome>
  );
}
